// IND-020: «Por qué este stock» del detalle del material: la misma frase que
// el Panel y «Revisa tus ficheros», con los productos que lo consumen desde el
// recuento. El desglose se pide al abrir el detalle (no va en el Panel).
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { getMaterialConsumptionBreakdown } from '../services/industrialCostService';
import type { MaterialConsumptionConsumer } from '../services/industrialCostService';
import type { MaterialStockProjectionRow } from '../types/industrialCost';
import { formatDataDay } from '../utils/dailyLoadRunFile';
import { esNumberFormat } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import SortableHeaderCell, { TEN_ROWS_MAX_HEIGHT } from '../components/SortableHeaderCell';

const qty = esNumberFormat({ maximumFractionDigits: 1 });
const perUnit = esNumberFormat({ maximumFractionDigits: 4 });

function consumerLabel(consumer: MaterialConsumptionConsumer): string {
  const product = consumer.product_code || consumer.product_name || '—';
  return consumer.customer_number ? `${product} · cliente ${consumer.customer_number}` : product;
}

// IND-026: orden por cabecera; por defecto, lo que más consume.
type ConsumerSortKey = 'product' | 'produced' | 'perUnit' | 'total';
const CONSUMER_COLUMNS: Array<{ key: ConsumerSortKey; label: string; value: (row: MaterialConsumptionConsumer) => SortValue }> = [
  { key: 'product', label: 'Producto', value: consumerLabel },
  { key: 'produced', label: 'Fabricado', value: (row) => row.produced_qty },
  { key: 'perUnit', label: 'Consumo por unidad', value: (row) => row.consumption_per_unit },
  { key: 'total', label: 'Total', value: (row) => row.consumed_qty },
];

export default function IndustrialCostStockExplanationBlock({
  material,
}: {
  material: MaterialStockProjectionRow;
}) {
  const explained = Boolean(material.stock_explanation);
  const query = useQuery({
    queryKey: ['industrial-costs', 'material-consumption', material.material_id],
    queryFn: () => getMaterialConsumptionBreakdown(material.material_id),
    enabled: explained,
    staleTime: 60_000,
    retry: false,
  });

  const sort = useTableSort<ConsumerSortKey>('total', 'desc');

  if (!explained && !material.baseline_assumed_zero) return null;
  const unit = material.unit_label || '';
  const sortColumn = CONSUMER_COLUMNS.find((column) => column.key === sort.key) ?? CONSUMER_COLUMNS[3];
  const consumers = sortRows(query.data?.consumers ?? [], sortColumn.value, sort.direction);

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: 'rgba(251,191,36,0.06)',
        border: '1px solid rgba(251,191,36,0.25)',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ color: '#fbbf24', fontWeight: 900, fontSize: 12 }}>Por qué este stock</Typography>
      {material.baseline_assumed_zero ? (
        <Typography sx={{ color: '#cbd5e1', fontSize: 12, mt: 0.25 }}>
          {`No está en el recuento del ${formatDataDay(material.baseline_count_date)?.slice(0, 5) ?? '—'}: se toma 0` +
            (material.previous_count_date
              ? ` (último recuento con él: ${formatDataDay(material.previous_count_date)}).`
              : '.')}
        </Typography>
      ) : null}
      {explained ? (
        <>
          <Typography sx={{ color: '#e2e8f0', fontSize: 12.5, mt: 0.25 }}>{material.stock_explanation}.</Typography>
          {query.isLoading ? (
            <CircularProgress size={16} sx={{ mt: 1 }} />
          ) : query.isError ? (
            <Typography sx={{ color: '#94a3b8', fontSize: 12, mt: 0.5 }}>
              No se pudo cargar qué productos lo consumen.
            </Typography>
          ) : consumers.length === 0 ? (
            <Typography sx={{ color: '#94a3b8', fontSize: 12, mt: 0.5 }}>
              Ningún producto lo ha consumido desde el recuento.
            </Typography>
          ) : (
            <Box sx={{ mt: 0.75, maxHeight: TEN_ROWS_MAX_HEIGHT, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {CONSUMER_COLUMNS.map((column) => (
                      <SortableHeaderCell
                        key={column.key}
                        label={column.label}
                        columnKey={column.key}
                        sort={sort}
                        firstDirection={column.key === 'product' ? 'asc' : 'desc'}
                        sx={{ bgcolor: '#0f172a', color: '#94a3b8', fontSize: 11, py: 0.5 }}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {consumers.map((consumer) => (
                    <TableRow key={`${consumer.product_id}-${consumer.customer_number ?? ''}-${consumer.basis}`}>
                      <TableCell sx={{ color: '#e2e8f0', fontSize: 12, py: 0.4 }}>{consumerLabel(consumer)}</TableCell>
                      <TableCell sx={{ color: '#cbd5e1', fontSize: 12, py: 0.4, whiteSpace: 'nowrap' }}>
                        {`${qty.format(consumer.produced_qty)} ${consumer.basis_unit}`}
                      </TableCell>
                      <TableCell sx={{ color: '#cbd5e1', fontSize: 12, py: 0.4 }}>
                        {consumer.consumption_per_unit != null
                          ? `${perUnit.format(consumer.consumption_per_unit)} ${unit}/${consumer.basis_unit}`
                          : '—'}
                        {consumer.multiple_rules ? ' · varias reglas' : ''}
                        {consumer.unit_mismatch
                          ? ` · la regla está en ${consumer.rule_unit_label} y el material en ${unit || '—'}`
                          : ''}
                      </TableCell>
                      <TableCell sx={{ color: '#f8fafc', fontSize: 12, py: 0.4, fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {`${qty.format(consumer.consumed_qty)} ${unit}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      ) : null}
    </Box>
  );
}
